import {
    mapIconPreviewIndoorStandLogoToInitialDate,
    mapIconPreviewIndoorStandPinToInitialDate,
    useIconPreview
} from 'hooks/marker/useIconPreview';
import { IIconV2WorldMarker, IIndoorStandWorldMarker, IWorldMarker } from 'react-give-me-map';
import { MapRef } from 'react-map-gl';
import styled from 'styled-components/macro';

interface SingleProps<T extends IIconV2WorldMarker | IIndoorStandWorldMarker> {
    mapRef: React.RefObject<MapRef>;
    markers: IWorldMarker[];
    marker: T;
}

const SingleLazyImageFallbackForIcon = (props: SingleProps<IIconV2WorldMarker>) => {
    const iconPreview = useIconPreview({
        mapRef: props.mapRef,
        worldMarkers: props.markers,
        markerIds: [props.marker.id],
        initialData: props.marker.elementData,

        markerType: 'icon'
    });

    return <S_Canvas ref={iconPreview.canvasRef} width={200} height={200} />;
};

const SingleLazyImageFallbackForIndoorPin = (props: SingleProps<IIndoorStandWorldMarker>) => {
    const iconPreview = useIconPreview({
        mapRef: props.mapRef,
        worldMarkers: props.markers,
        markerIds: [props.marker.id],
        initialData: mapIconPreviewIndoorStandPinToInitialDate(props.marker.elementData),
        markerType: 'icon'
    });

    return <S_Canvas ref={iconPreview.canvasRef} width={200} height={200} />;
};

const SingleLazyImageFallbackForIndoorLogo = (props: SingleProps<IIndoorStandWorldMarker>) => {
    const iconPreview = useIconPreview({
        mapRef: props.mapRef,
        worldMarkers: props.markers,
        markerIds: [props.marker.id],
        iconPrefix: 'temp-icon|',
        initialData: mapIconPreviewIndoorStandLogoToInitialDate(props.marker.elementData),
        markerType: 'icon'
    });

    return <S_Canvas ref={iconPreview.canvasRef} width={200} height={90} />;
};

interface Props {
    mapRef: React.RefObject<MapRef>;
    markers: IWorldMarker[];
}

export const LazyImageFallback = (props: Props) => {
    const notFinishedIconMarkers = props.markers.filter<IIconV2WorldMarker>(
        (e): e is IIconV2WorldMarker =>
            e.elementType === 'v2/icon' && e.elementData.imageUrl?.startsWith('temp-icon')
    );
    const notFinishedIndoorMarkersForPing = props.markers.filter<IIndoorStandWorldMarker>(
        (e): e is IIndoorStandWorldMarker =>
            e.elementType === 'indoor_stand' && e.elementData.pinSrc?.startsWith('temp-icon')
    );
    const notFinishedIndoorMarkersForLogo = props.markers.filter<IIndoorStandWorldMarker>(
        (e): e is IIndoorStandWorldMarker =>
            e.elementType === 'indoor_stand' && e.elementData.imageSrc?.startsWith('temp-icon')
    );

    return (
        <>
            {notFinishedIconMarkers.map((marker) => (
                <SingleLazyImageFallbackForIcon
                    key={marker.id}
                    mapRef={props.mapRef}
                    markers={props.markers}
                    marker={marker}
                />
            ))}
            {notFinishedIndoorMarkersForPing.map((marker) => (
                <SingleLazyImageFallbackForIndoorPin
                    key={marker.id}
                    mapRef={props.mapRef}
                    markers={props.markers}
                    marker={marker}
                />
            ))}
            {notFinishedIndoorMarkersForLogo.map((marker) => (
                <SingleLazyImageFallbackForIndoorLogo
                    key={marker.id}
                    mapRef={props.mapRef}
                    markers={props.markers}
                    marker={marker}
                />
            ))}
        </>
    );
};

const S_Canvas = styled.canvas`
    width: 100px;
    height: 100px;
    display: none;
`;
